import { DocumentUploadSource } from "../../../api/graphql/schema.generated";
import type { ReceiptSourceFacetsQuery } from "../../../api/graphql/queries/receiptSourceFacets/receiptSourceFacets.generated";
import {
  CHANNEL_ORIGIN_IDS,
  PROVIDER_ORIGIN_IDS,
  getOriginPresentation,
} from "../../../utils/colorClassVariants";

/**
 * Reserved origin id for receipts synced from smarta-documents that carry no
 * `targetSystem` — rows created before the SMT-1108 backfill. Must match
 * DOCUMENT_ORIGIN_NONE in kontax-backend's documentMatching/types.ts; without it
 * they would be the one group unreachable from this filter.
 */
export const DOCUMENT_ORIGIN_NONE = "__none__";

/**
 * One entry in the Receipt Overview source filter. `key` is what the dropdown
 * and the URL carry; `uploadSources` / `documentOrigins` are what it actually
 * matches on the backend, which is deliberately not 1:1:
 *
 *  - Web and Mobile contribute to BOTH families. Tax Ops has no reason to care
 *    whether a web upload was recorded by kontax or by the OCR pipeline, so one
 *    option covers both (SMT-1191, confirmed with Stella).
 *  - E-Mail is the exception: the new forwarding flow (origin `email`) and the
 *    legacy shared inbox (upload source EMAIL) stay separate options, because
 *    Tax Ops does want to tell those apart.
 *
 * The backend ORs the two families, so an option spanning both widens the result
 * rather than narrowing it to nothing.
 */
export type SourceFilterOption = {
  key: string;
  /** i18n key, resolved by the caller against the ReceiptOverview namespace. */
  labelKey?: string;
  /** Literal label for provider names, which are brand names and untranslated. */
  label?: string;
  uploadSources?: DocumentUploadSource[];
  documentOrigins?: string[];
};

/**
 * SMT-1238: the facet shape the Backoffice needs from `receiptSourceFacets`.
 * Re-exported under this repo's own name (rather than importing the generated
 * type at every call site) so callers/tests are unaffected by where the type
 * actually comes from — this now aliases the generated
 * `ReceiptSourceFacetsQuery["receiptSourceFacets"]`, kept in sync with the
 * GraphQL contract by codegen instead of by hand.
 */
export type ReceiptSourceFacets =
  ReceiptSourceFacetsQuery["receiptSourceFacets"];

/**
 * Turns a raw provider origin id into its filter option. Shared by the static
 * `providerOptions` below and by `buildSourceFilterOptions` (SMT-1238), so the
 * label rule — brand name where known, raw id otherwise — is written once.
 */
const toProviderOption = (id: string): SourceFilterOption => {
  const presentation = getOriginPresentation(id);
  return {
    key: id,
    // Falls back to the raw id, matching how SourceBadge renders an origin it
    // does not recognise — never masked behind a friendlier but wrong name.
    label: presentation?.kind === "provider" ? presentation.name : id,
    documentOrigins: [id],
  };
};

const providerOptions: SourceFilterOption[] =
  PROVIDER_ORIGIN_IDS.map(toProviderOption);

/**
 * The option for receipts whose origin was never recorded. Defined once so
 * both the static Integration group and `buildSourceFilterOptions` (SMT-1238)
 * offer the exact same option rather than two objects that happen to agree.
 */
const NONE_OPTION: SourceFilterOption = {
  key: DOCUMENT_ORIGIN_NONE,
  labelKey: "filter.sourceUnknownIntegration",
  documentOrigins: [DOCUMENT_ORIGIN_NONE],
};

/**
 * Children of the Integration group: every provider, plus the receipts whose
 * origin was never recorded. Listing the latter explicitly (rather than folding
 * it invisibly into the parent) keeps "the parent equals all of its children"
 * true, which is what makes the group predictable.
 */
export const INTEGRATION_GROUP_OPTIONS: SourceFilterOption[] = [
  ...providerOptions,
  NONE_OPTION,
];

const CHANNEL_LABEL_KEYS: Record<string, string> = {
  email: "origin.email",
  web: "source.web",
  mobile: "source.mobile",
};

/**
 * The flat options shown alongside the Integration group, in display order.
 * EMAIL_FETCH stays out, as it has since SMT-903: it is not a channel anyone
 * uploads through, and with a static universe a dead option would just sit in
 * the list unfilterable-in-practice for every customer.
 *
 * SMT-1238 reviewer finding: that reasoning covers this *static* fallback list
 * only — it does not extend to the per-customer dynamic list. A customer can
 * have real EMAIL_FETCH receipts (pre-migration data), and for them the option
 * is not dead at all; leaving it out there would make those receipts
 * unfilterable, which fails the ticket's "no receipt becomes unfilterable" AC.
 * See `FETCH_LEGACY_OPTION` and `buildSourceFilterOptions`, which offer it
 * exactly when EMAIL_FETCH is in the facets (or actively selected).
 */
export const STANDALONE_SOURCE_OPTIONS: SourceFilterOption[] = [
  {
    key: "taxops",
    labelKey: "source.taxops",
    uploadSources: [DocumentUploadSource.BACKOFFICE],
  },
  {
    key: "email",
    labelKey: CHANNEL_LABEL_KEYS.email,
    documentOrigins: ["email"],
  },
  {
    key: "email-legacy",
    labelKey: "source.email",
    uploadSources: [DocumentUploadSource.EMAIL],
  },
  {
    key: "web",
    labelKey: CHANNEL_LABEL_KEYS.web,
    uploadSources: [DocumentUploadSource.WEB],
    documentOrigins: ["web"],
  },
  {
    key: "mobile",
    labelKey: CHANNEL_LABEL_KEYS.mobile,
    uploadSources: [DocumentUploadSource.MOBILE],
    documentOrigins: ["mobile"],
  },
];

export const ALL_SOURCE_OPTIONS: SourceFilterOption[] = [
  ...STANDALONE_SOURCE_OPTIONS,
  ...INTEGRATION_GROUP_OPTIONS,
];

/**
 * SMT-1238 reviewer finding: the option that makes EMAIL_FETCH receipts
 * filterable for a customer who actually has them. Deliberately not part of
 * `STANDALONE_SOURCE_OPTIONS` (see the doc comment there) — it only enters the
 * candidate list `buildSourceFilterOptions` filters against, so the static
 * fallback (and everything built from `ALL_SOURCE_OPTIONS`) is unaffected.
 */
export const FETCH_LEGACY_OPTION: SourceFilterOption = {
  key: "fetch-legacy",
  labelKey: "source.fetch",
  uploadSources: [DocumentUploadSource.EMAIL_FETCH],
};

/**
 * The standalone candidates `buildSourceFilterOptions` filters against:
 * `STANDALONE_SOURCE_OPTIONS` plus `FETCH_LEGACY_OPTION`, spliced in right
 * after `email-legacy` so display order matches the static list's own
 * grouping of the two e-mail-shaped options. Computed by index (rather than
 * simply appended) so this stays correct if `STANDALONE_SOURCE_OPTIONS` is
 * ever reordered.
 */
const EMAIL_LEGACY_INDEX = STANDALONE_SOURCE_OPTIONS.findIndex(
  (option) => option.key === "email-legacy"
);
const STANDALONE_CANDIDATE_OPTIONS: SourceFilterOption[] = [
  ...STANDALONE_SOURCE_OPTIONS.slice(0, EMAIL_LEGACY_INDEX + 1),
  FETCH_LEGACY_OPTION,
  ...STANDALONE_SOURCE_OPTIONS.slice(EMAIL_LEGACY_INDEX + 1),
];

/**
 * Turns the selected option keys into the two backend filter lists, deduped.
 *
 * SMT-1238: takes an optional `options` list, defaulting to the static
 * `ALL_SOURCE_OPTIONS` universe so every existing caller is unaffected. A
 * caller with the dynamic per-customer list from `buildSourceFilterOptions`
 * passes it here so an unknown provider key (not in the static map) still
 * resolves to `documentOrigins: [key]` instead of being silently dropped.
 */
export const toFilterValues = (
  selectedKeys: string[],
  options: SourceFilterOption[] = ALL_SOURCE_OPTIONS
): {
  uploadSources?: DocumentUploadSource[];
  documentOrigins?: string[];
} => {
  const uploadSources = new Set<DocumentUploadSource>();
  const documentOrigins = new Set<string>();

  selectedKeys.forEach((key) => {
    const option = options.find((entry) => entry.key === key);
    option?.uploadSources?.forEach((source) => uploadSources.add(source));
    option?.documentOrigins?.forEach((origin) => documentOrigins.add(origin));
  });

  return {
    uploadSources: uploadSources.size ? Array.from(uploadSources) : undefined,
    documentOrigins: documentOrigins.size
      ? Array.from(documentOrigins)
      : undefined,
  };
};

/**
 * Whether any of an option's contributions is present in the given filter
 * values. Shared by `toSelectedKeys` (values = what is currently selected) and
 * `buildSourceFilterOptions` (values = the candidate sources/origins a
 * customer could have, SMT-1238) so the "any contribution, not all" rule is
 * expressed once.
 */
const hasAnyContribution = (
  option: SourceFilterOption,
  uploadSources: Set<DocumentUploadSource>,
  documentOrigins: Set<string>
): boolean =>
  Boolean(
    option.uploadSources?.some((source) => uploadSources.has(source)) ||
      option.documentOrigins?.some((origin) => documentOrigins.has(origin))
  );

/**
 * The inverse: which options a stored filter corresponds to. An option counts as
 * selected when ANY of its contributions is present, not all of them, so a link
 * shared before SMT-1191 (`?uploadSources=WEB`, with no origins) still shows Web
 * ticked instead of an active-but-invisible filter. Touching the dropdown then
 * normalises it to both halves.
 *
 * SMT-1238: takes an optional `options` list, defaulting to the static
 * `ALL_SOURCE_OPTIONS` universe so every existing caller is unaffected.
 */
export const toSelectedKeys = (
  uploadSources?: DocumentUploadSource[] | null,
  documentOrigins?: string[] | null,
  options: SourceFilterOption[] = ALL_SOURCE_OPTIONS
): string[] => {
  const sources = new Set(uploadSources ?? []);
  const origins = new Set(documentOrigins ?? []);

  return options
    .filter((option) => hasAnyContribution(option, sources, origins))
    .map((option) => option.key);
};

/**
 * SMT-1238 reviewer finding: normalises the pre-SMT-1191 shape of the
 * "Integration" filter. Before SMT-1191 split synced receipts into
 * per-provider origins, `?uploadSources=SMARTA_DOCUMENTS` meant "every synced
 * receipt" — the whole Integration group, not a provider. The dropdown has
 * not produced that value since SMT-1191 (neither `STANDALONE_SOURCE_OPTIONS`
 * nor `INTEGRATION_GROUP_OPTIONS` carries `SMARTA_DOCUMENTS` as an
 * `uploadSources` contribution any more), so a link that still carries it
 * filters the backend query while the source dropdown shows "All sources"
 * with nothing ticked — an active filter that is invisible to the operator
 * (SMT-1238 reviewer finding on PR #594). This translates it to the
 * equivalent provider-origin selection instead: dropping `SMARTA_DOCUMENTS`
 * and adding every origin `INTEGRATION_GROUP_OPTIONS` contributes (which
 * already includes the reserved `DOCUMENT_ORIGIN_NONE` id via `NONE_OPTION`).
 * It is deliberately not re-added as a selectable option — the UI must never
 * produce it again, only translate it away when it shows up in a link.
 *
 * Other upload sources already in the link (e.g. `WEB`) are left as they are.
 *
 * One known imprecision: a provider id the Backoffice map does not recognise
 * (not in `PROVIDER_ORIGIN_IDS`, so not part of `INTEGRATION_GROUP_OPTIONS`)
 * is not part of the translated selection — the legacy value only expands
 * into origins this file already knows about.
 */
export const normalizeLegacySourceFilters = (
  uploadSources?: DocumentUploadSource[] | null,
  documentOrigins?: string[] | null
): {
  uploadSources?: DocumentUploadSource[];
  documentOrigins?: string[];
  changed: boolean;
} => {
  const sources = uploadSources ?? [];
  const origins = documentOrigins ?? [];

  if (!sources.includes(DocumentUploadSource.SMARTA_DOCUMENTS)) {
    return {
      uploadSources: sources.length ? sources : undefined,
      documentOrigins: origins.length ? origins : undefined,
      changed: false,
    };
  }

  const remainingSources = sources.filter(
    (source) => source !== DocumentUploadSource.SMARTA_DOCUMENTS
  );

  const mergedOrigins = new Set(origins);
  INTEGRATION_GROUP_OPTIONS.forEach((option) => {
    option.documentOrigins?.forEach((origin) => mergedOrigins.add(origin));
  });

  return {
    uploadSources: remainingSources.length ? remainingSources : undefined,
    documentOrigins: mergedOrigins.size ? Array.from(mergedOrigins) : undefined,
    changed: true,
  };
};

/**
 * SMT-1238: builds the source filter options for one customer from the facets
 * the backend reports (what the customer actually has receipts from), unioned
 * with whatever is currently active in the URL/filter state — so a selection
 * from a shared link never disappears just because it is not (or no longer)
 * in the facets. See SMT-1238 for the full rule set.
 *
 * `standalone` reuses the exact "any contribution present" predicate
 * `toSelectedKeys` uses, so e.g. Web is offered whether the candidate carries
 * `WEB` as an upload source or `web` as an origin. It filters
 * `STANDALONE_CANDIDATE_OPTIONS` (SMT-1238: `STANDALONE_SOURCE_OPTIONS` plus
 * `FETCH_LEGACY_OPTION`), so Fetch (legacy) is offered exactly when EMAIL_FETCH
 * is itself a candidate — unlike the static list, which excludes it always.
 *
 * `integration` is the candidate origins minus the channel ids (email/web/
 * mobile — never providers) minus the reserved NONE id, turned into provider
 * options ordered by `PROVIDER_ORIGIN_IDS` first (known providers, in their
 * canonical order) then any unrecognised id alphabetically — same fallback
 * `SourceBadge` uses for an origin the Backoffice map does not know yet.
 * The NONE option is appended last, only when NONE is itself a candidate.
 */
export const buildSourceFilterOptions = ({
  facets,
  activeUploadSources,
  activeDocumentOrigins,
}: {
  facets: ReceiptSourceFacets;
  activeUploadSources?: DocumentUploadSource[] | null;
  activeDocumentOrigins?: string[] | null;
}): { standalone: SourceFilterOption[]; integration: SourceFilterOption[] } => {
  const candidateUploadSources = new Set<DocumentUploadSource>([
    ...facets.uploadSources,
    ...(activeUploadSources ?? []),
  ]);
  const candidateDocumentOrigins = new Set<string>([
    ...facets.documentOrigins,
    ...(activeDocumentOrigins ?? []),
  ]);

  const standalone = STANDALONE_CANDIDATE_OPTIONS.filter((option) =>
    hasAnyContribution(option, candidateUploadSources, candidateDocumentOrigins)
  );

  const candidateProviderIds = Array.from(candidateDocumentOrigins).filter(
    (id) => !CHANNEL_ORIGIN_IDS.includes(id) && id !== DOCUMENT_ORIGIN_NONE
  );
  const knownProviderIds = PROVIDER_ORIGIN_IDS.filter((id) =>
    candidateProviderIds.includes(id)
  );
  const unknownProviderIds = candidateProviderIds
    .filter((id) => !PROVIDER_ORIGIN_IDS.includes(id))
    .sort();

  const integration: SourceFilterOption[] = [
    ...knownProviderIds.map(toProviderOption),
    ...unknownProviderIds.map(toProviderOption),
  ];
  if (candidateDocumentOrigins.has(DOCUMENT_ORIGIN_NONE)) {
    integration.push(NONE_OPTION);
  }

  return { standalone, integration };
};
